import moment from 'moment';
import { LLM_MODELS, LLM_MODEL_FAMILY_CANDIDATES } from './models.js';
import { LlmModelLifecycleState, } from './types.js';
/**
 * A `YYYY-MM-DD` plain date is "past" once its UTC midnight is at or before now.
 * Parsed with `moment.utc` (the repo's plain-date convention) so the comparison
 * never shifts a day for clients west of UTC.
 */
function isPastPlainDate(date) {
    return moment.utc(date, 'YYYY-MM-DD', true).isSameOrBefore(moment.utc());
}
/** Thrown when every candidate of a successor family is upcoming or obsolete. */
export class LlmModelNoExecutableCandidateError extends Error {
    modelFamily;
    constructor(modelFamily) {
        super(`LLM model family "${modelFamily}" has no executable candidate`);
        this.name = 'LlmModelNoExecutableCandidateError';
        this.modelFamily = modelFamily;
    }
}
/**
 * Derives a model's lifecycle state. Precedence: `Obsolete` (past the obsolete
 * date, or `forceObsolete`) > `Upcoming` (before `addedDate`) > `Retired` (past
 * the retired date) > `Active`.
 */
export function getLlmModelLifecycleState(model) {
    const { retiredDate, obsoleteDate, forceObsolete } = model.lifecycle;
    if (forceObsolete === true || (obsoleteDate !== null && isPastPlainDate(obsoleteDate))) {
        return LlmModelLifecycleState.Obsolete;
    }
    if (model.addedDate !== undefined && !isPastPlainDate(model.addedDate)) {
        return LlmModelLifecycleState.Upcoming;
    }
    if (retiredDate !== null && isPastPlainDate(retiredDate)) {
        return LlmModelLifecycleState.Retired;
    }
    return LlmModelLifecycleState.Active;
}
/**
 * Whether a model may be offered for selection and executed: `Active` or
 * `Retired` (still callable). `Upcoming` and `Obsolete` models are neither.
 */
export function isLlmModelOfferable(model) {
    const state = getLlmModelLifecycleState(model);
    return state === LlmModelLifecycleState.Active || state === LlmModelLifecycleState.Retired;
}
/** Returns the first executable candidate of a model's lifecycle successor family. */
export function getLlmModelSuccessor(model) {
    const modelFamily = model.lifecycle.successorModelFamily;
    const modelName = LLM_MODEL_FAMILY_CANDIDATES[modelFamily].find((candidate) => isLlmModelOfferable(LLM_MODELS[candidate]));
    if (modelName === undefined) {
        throw new LlmModelNoExecutableCandidateError(modelFamily);
    }
    return { modelFamily, modelName };
}
/**
 * Resolves the model used at execution time. Obsolete and upcoming models move
 * one hop to their successor family's first executable candidate; Active and
 * Retired models pass through unchanged.
 */
export function resolveLlmModelForExecution(model) {
    const state = getLlmModelLifecycleState(model);
    switch (state) {
        case LlmModelLifecycleState.Active:
        case LlmModelLifecycleState.Retired:
            return { executionModel: model.id };
        case LlmModelLifecycleState.Obsolete:
        case LlmModelLifecycleState.Upcoming:
            return { executionModel: getLlmModelSuccessor(model).modelName, fallbackReason: state };
        default: {
            const unhandled = state;
            throw new Error(`Unhandled LLM model lifecycle state: ${String(unhandled)}`);
        }
    }
}
//# sourceMappingURL=lifecycle.js.map